/**
 * OwnGoalPlacementHeader — EPIC-98 (item 5/AC5). The own-goal naming that
 * `GoalPlacementOverlay.tsx`'s dialog header shows for the LIVE goal-frame
 * modal, extracted so the review/feed placement editor (`ReviewView.tsx`,
 * `LiveEventFeed.tsx` — both render `GoalFramePlacement` directly, with no
 * header of their own) can show the same naming above their panel without
 * duplicating the crest + text composition.
 *
 * Renders nothing for a non-own-goal event, and nothing when the conceding
 * player's team can't yet be resolved from either roster (defensive — a
 * placement editor must never crash on a stale/incomplete roster) — safe to
 * mount unconditionally above any placement control.
 *
 * The conceding player is always `event.player_id` — an own goal never
 * carries a taker-correction control (that's Penalty-only), so there is no
 * `currentTakerId` override to thread through here.
 */
import type { TFunction } from "i18next";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { Match } from "../../types";
import { formatDisplayName } from "../../../../lib/nameFormat";
import TeamLogo from "../../../../components/TeamLogo";
import { findPlayerTeam } from "../../utils/ownGoalTeam";

export interface OwnGoalPlacementHeaderProps {
  event: MatchEvent;
  match: Match;
  t: TFunction<"logger">;
  className?: string;
}

const OwnGoalPlacementHeader = ({
  event,
  match,
  t,
  className,
}: OwnGoalPlacementHeaderProps) => {
  if (event.data?.own_goal !== true) return null;

  const concedingTeam = findPlayerTeam(event.player_id, match);
  if (!concedingTeam) return null;

  const player = [...match.home_team.players, ...match.away_team.players].find(
    (p) => p.id === event.player_id,
  );
  const playerDisplay = player
    ? `#${player.jersey_number} ${formatDisplayName(player.full_name)}`
    : "";

  const headerText = [
    t("goalPlacementOverlay.ownGoalLabel", "Own goal"),
    t("goalPlacementOverlay.ownGoalTeamLabel", "{{team}}'s own goal", {
      team: concedingTeam.short_name || concedingTeam.name,
    }),
    playerDisplay,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      data-testid="own-goal-placement-header"
      className={`flex items-center gap-2 ${className ?? ""}`}
    >
      <TeamLogo
        team={concedingTeam}
        size="xs"
        className="shrink-0"
        testId="own-goal-placement-header-crest"
      />
      <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-200">
        {headerText}
      </span>
    </div>
  );
};

export default OwnGoalPlacementHeader;
